import type { ReactNode } from "react";
import { Component } from "react";
import { Button } from "./Button.tsx";
import {
  COLOR_BG,
  COLOR_DANGER,
  COLOR_TEXT,
  COLOR_TEXT_DIM,
  FONT_SIZE_BODY,
} from "./tokens.ts";

interface State {
  error: Error | null;
}

/**
 * Top-level error boundary that catches unhandled React render errors and
 * displays a recovery screen instead of a blank white page.
 */
export class AppErrorBoundary extends Component<
  { children: ReactNode },
  State
> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error(
      "[AppErrorBoundary] Unhandled render error:",
      error,
      info.componentStack,
    );
  }

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <div
        style={{
          position: "fixed",
          inset: 0,
          background: COLOR_BG,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 32,
        }}
      >
        <div
          style={{
            maxWidth: 480,
            display: "flex",
            flexDirection: "column",
            gap: 16,
            textAlign: "center",
          }}
        >
          <span style={{ fontSize: 20, fontWeight: 600, color: COLOR_TEXT }}>
            Something went wrong
          </span>
          <p
            style={{
              fontSize: FONT_SIZE_BODY,
              color: COLOR_TEXT_DIM,
              lineHeight: 1.5,
            }}
          >
            PoseRef encountered an unexpected error. Your work has been
            preserved in the cloud — reloading should restore your session.
          </p>
          <pre
            style={{
              fontSize: 11,
              color: COLOR_DANGER,
              background: "rgba(255,255,255,0.04)",
              borderRadius: 8,
              padding: 12,
              textAlign: "left",
              overflow: "auto",
              maxHeight: 160,
              whiteSpace: "pre-wrap",
              wordBreak: "break-all",
            }}
          >
            {this.state.error.message}
          </pre>
          <Button
            type="button"
            onClick={() => window.location.reload()}
            variant="primary"
            size="md"
            style={{ alignSelf: "center", paddingInline: 28 }}
          >
            Reload PoseRef
          </Button>
        </div>
      </div>
    );
  }
}
